import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { getHeaders } from "../ApiHeaders/apiHeadersEntitlements";
import { FaSearch } from "react-icons/fa";

interface Item {
  id: string;
  name: string;
  domain?: string;
  itemDomain?: string;
}

interface Props {
  fetchUrl: string;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  idField: string;
  nameField: string;
  domainField?: string; // optional
  allowedDomains?: string[]; // optional: only list items whose `domain` is one of these
  disabled?: boolean;
  preserveItemOrder?: boolean;
  reorderSelectedOnOutside?: boolean;
}

const SearchableMappingList: React.FC<Props> = ({
  fetchUrl,
  selectedIds,
  onSelectionChange,
  idField,
  nameField,
  domainField,
  allowedDomains,
  disabled = false,
  preserveItemOrder = true,
  reorderSelectedOnOutside = false,
}) => {
  const allowedDomainsKey = allowedDomains?.join(",");
  const [items, setItems] = useState<Item[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFirstMode, setSelectedFirstMode] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState<string[]>([]);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setSearchTerm("");
        if (reorderSelectedOnOutside) {
          setSelectedSnapshot(selectedIds);
          setSelectedFirstMode(true);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [reorderSelectedOnOutside, selectedIds]);

  useEffect(() => {
    if (disabled && reorderSelectedOnOutside) {
      setSelectedSnapshot(selectedIds);
      setSelectedFirstMode(true);
    }
  }, [disabled, reorderSelectedOnOutside, selectedIds]);

  // =====================================================
  // FETCH DATA
  // =====================================================
  useEffect(() => {
    const fetchItems = async () => {
      const headers = await getHeaders();
      const response = await axios.get(fetchUrl, { headers });

      if (response.data?.response?.data) {
        const mapped: Item[] = response.data.response.data.map(
          (item: any) => ({
            id: String(item[idField]),
            name: item[nameField],
            domain: domainField ? item[domainField] : undefined,
            itemDomain: item.domain,
          })
        );

        setItems(mapped);
      }
    };

    fetchItems();
  }, [fetchUrl, idField, nameField, domainField]);

  // Hidden items stay in selectedIds: toggling only adds/removes what is shown.
  const visibleItems = useMemo(() => {
    if (!allowedDomainsKey) return items;
    const allowed = allowedDomainsKey.split(",");
    return items.filter((item) =>
      allowed.includes((item.itemDomain || "").toLowerCase())
    );
  }, [items, allowedDomainsKey]);

  // =====================================================
  // FILTER + SORT (selected first like Users box)
  // =====================================================
  const filteredItems = useMemo(() => {
    const query = searchTerm.toLowerCase();

    const matchesQuery = (item: Item) => item.name.toLowerCase().includes(query);

    if (selectedFirstMode && reorderSelectedOnOutside) {
      const snapshotSet = new Set(selectedSnapshot);

      if (preserveItemOrder) {
        const selected = visibleItems.filter((item) => snapshotSet.has(item.id) && matchesQuery(item));
        const unselected = visibleItems.filter((item) => !snapshotSet.has(item.id) && matchesQuery(item));
        return [...selected, ...unselected];
      }

      const selected = visibleItems
        .filter((item) => snapshotSet.has(item.id) && matchesQuery(item))
        .sort((a, b) => a.name.localeCompare(b.name));

      const unselected = visibleItems
        .filter((item) => !snapshotSet.has(item.id) && matchesQuery(item))
        .sort((a, b) => a.name.localeCompare(b.name));

      return [...selected, ...unselected];
    }

    if (preserveItemOrder) {
      return visibleItems.filter((item) => matchesQuery(item));
    }

    const selectedSet = new Set(selectedIds);

    const selected = visibleItems
      .filter(
        (item) =>
          selectedSet.has(item.id) &&
            matchesQuery(item)
      )
      .sort((a, b) => a.name.localeCompare(b.name));

    const unselected = visibleItems
      .filter(
        (item) =>
          !selectedSet.has(item.id) &&
          matchesQuery(item)
      )
      .sort((a, b) => a.name.localeCompare(b.name));

    return [...selected, ...unselected];
  }, [
    visibleItems,
    selectedIds,
    searchTerm,
    preserveItemOrder,
    reorderSelectedOnOutside,
    selectedFirstMode,
    selectedSnapshot,
  ]);

  // =====================================================
  // SELECTION
  // =====================================================
  const isSelected = (id: string) => selectedIds.includes(id);

  const toggleItemSelection = (id: string) => {
    const updated = isSelected(id)
      ? selectedIds.filter((i) => i !== id)
      : [...selectedIds, id];

    onSelectionChange(updated);
  };

  // =====================================================
  // DYNAMIC COLUMN LAYOUT
  // =====================================================
//   const columnClass = domainField
//     ? "grid grid-cols-3 items-center gap-x-3"
//     : "grid grid-cols-2 items-center gap-x-3";
const columnClass = domainField
  ? "grid items-center gap-x-3 [grid-template-columns:2fr_1.5fr_auto]"
  : "grid items-center gap-x-3 [grid-template-columns:1fr_auto]";

  return (
    <div ref={wrapperRef} className="space-y-4 w-full p-4">
      {/* ================= SEARCH BOX (EXACT USERS STYLE) ================= */}
      <div className="relative w-full">
        <FaSearch className="absolute top-3.5 left-3 text-gray-500 w-5 h-5" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:border-blue-500 bg-gray-50 text-gray-800 shadow-inner text-sm"
          placeholder="Search here"
          disabled={disabled && false} // still searchable in view mode
        />
      </div>

      {/* ================= ITEMS CONTAINER (EXACT USERS STYLE) ================= */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow min-h-36 max-h-[18rem] overflow-y-auto">
        {filteredItems.length > 0 ? (
          <>
            {/* HEADER */}
            <div className={`${columnClass} text-gray-600 text-sm font-semibold mb-2`}>
              <span>Name</span>
              {domainField && <span>Domain</span>}
              <span className="text-right">Select</span>
            </div>

            {/* ROWS */}
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className={`${columnClass} py-2 hover:bg-blue-50 rounded-lg cursor-pointer`}
                onClick={() => !disabled && toggleItemSelection(item.id)}
              >
                <span className="text-gray-800 text-sm">
                  {item.name}
                </span>

                {domainField && (
                  <span className="text-gray-800 text-sm">
                    {item.domain || "—"}
                  </span>
                )}

                <div className="flex justify-end">
                  <input
                    type="checkbox"
                    checked={isSelected(item.id)}
                    onChange={() => toggleItemSelection(item.id)}
                    disabled={disabled}
                    className="form-checkbox w-4 h-4 text-blue-500"
                  />
                </div>
              </div>
            ))}
          </>
        ) : (
          <p className="text-gray-500 text-sm text-center">
            No items found.
          </p>
        )}
      </div>
    </div>
  );
};

export default SearchableMappingList;